/**
 * ReasoningSidebar Component
 *
 * Displays the AI's step-by-step reasoning process in a sidebar.
 * Uses ReasoningStepsList for consistent step rendering.
 *
 * Design based on Figma: node-id=2643-2711
 */

import { CubeOutline, X } from '@untitledui/icons';
import React from 'react';

import { REASONING_DESIGN_TOKENS } from './design-tokens';
import type { ReasoningStepData } from './ReasoningStepsList';
import { ReasoningStepsList } from './ReasoningStepsList';

interface Chain {
  id: string;
  user_message: string;
  status: string;
  steps: ReasoningStepData[];
}

interface ReasoningSidebarProps {
  chain: Chain | null;
  onClose: () => void;
  onOpenDynamicViewSidebar: (viewId: number) => void;
}

export const ReasoningSidebar: React.FC<ReasoningSidebarProps> = ({
  chain,
  onClose,
  onOpenDynamicViewSidebar,
}) => {
  if (!chain) {
    return null;
  }

  return (
    <div
      className={`h-full flex flex-col ${REASONING_DESIGN_TOKENS.colors.headerBg}`}
    >
      {/* Header */}
      <div className='shrink-0'>
        <div className='flex items-center justify-between px-4 py-3'>
          <div className='flex items-center gap-2'>
            <CubeOutline
              className={`w-6 h-6 ${REASONING_DESIGN_TOKENS.colors.headerTitle}`}
              strokeWidth={1.5}
            />
            <h2
              className={`text-xl font-medium ${REASONING_DESIGN_TOKENS.colors.headerTitle}`}
            >
              Reasoning
            </h2>
          </div>
          <button
            onClick={onClose}
            className='p-1 hover:bg-bg-secondary rounded-lg transition-colors cursor-pointer'
            aria-label='Close reasoning sidebar'
          >
            <X className='w-6 h-6 text-fg-primary' strokeWidth={1.5} />
          </button>
        </div>
        <div className='h-px w-full bg-bg-tertiary' />
      </div>

      {/* Content */}
      <div className='flex-1 overflow-y-auto px-4'>
        <ReasoningStepsList
          steps={chain.steps}
          listId={chain.id}
          onOpenDynamicViewSidebar={onOpenDynamicViewSidebar}
        />
      </div>
    </div>
  );
};

export default ReasoningSidebar;
